/**
 * ScenariosPanel - Panel de planificación con escenarios
 *
 * @version 1.0
 * @updated 2026-02-18
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Panel de la pestaña "Planificación" que permite crear, listar y activar
 * escenarios de análisis (baseline, proposed, sensitivity).
 *
 * Cuando un escenario está activo, se emite el evento `scenario-activated`
 * para que MapLayout muestre el ScenarioBanner en el mapa.
 * Al desactivar (desde el banner), se recibe `scenario-deactivated`.
 */

import { useState, useEffect, useCallback } from 'react';
import { GitBranch, Plus, Play, Square, Trash2, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useProject } from '../../../projects';
import { scenarioApi } from '../../../scenarios';
import type { Scenario, ScenarioType } from '../../../scenarios';
import './scenarios-panel.css';

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

const TYPE_LABELS: Record<ScenarioType, string> = {
  baseline:    'Base',
  proposed:    'Propuesta',
  sensitivity: 'Sensibilidad',
};

const TYPE_COLORS: Record<ScenarioType, string> = {
  baseline:    'badge-baseline',
  proposed:    'badge-proposed',
  sensitivity: 'badge-sensitivity',
};

// ─────────────────────────────────────────
// Create Form
// ─────────────────────────────────────────

interface CreateFormProps {
  projectId: string;
  onCreated: (scenario: Scenario) => void;
  onCancel: () => void;
}

function CreateForm({ projectId, onCreated, onCancel }: CreateFormProps): JSX.Element {
  const [name, setName]       = useState('');
  const [type, setType]       = useState<ScenarioType>('proposed');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const handleSubmit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed.length < 3) { setError('Mínimo 3 caracteres'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await scenarioApi.create({ projectId, name: trimmed, type });
      onCreated(created);
    } catch {
      setError('Error al crear el escenario');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sp-create-form">
      <input
        type="text"
        className="sp-input"
        placeholder="Nombre del escenario"
        value={name}
        maxLength={255}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') void handleSubmit(); if (e.key === 'Escape') onCancel(); }}
        autoFocus
      />
      <select
        className="sp-select"
        value={type}
        onChange={e => setType(e.target.value as ScenarioType)}
      >
        <option value="proposed">Propuesta</option>
        <option value="baseline">Línea de base</option>
        <option value="sensitivity">Sensibilidad</option>
      </select>
      {error && (
        <div className="sp-form-error">
          <AlertCircle size={12} /><span>{error}</span>
        </div>
      )}
      <div className="sp-form-actions">
        <button className="sp-btn sp-btn-ghost" onClick={onCancel} disabled={saving}>
          Cancelar
        </button>
        <button className="sp-btn sp-btn-primary" onClick={() => void handleSubmit()} disabled={saving}>
          {saving ? <Loader2 size={12} className="sp-spin" /> : <Plus size={12} />}
          Crear
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Scenario Card
// ─────────────────────────────────────────

interface ScenarioCardProps {
  scenario: Scenario;
  isActive: boolean;
  onActivate: (scenario: Scenario) => void;
  onDeactivate: () => void;
  onDelete: (id: string) => void;
}

function ScenarioCard({ scenario, isActive, onActivate, onDeactivate, onDelete }: ScenarioCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const canDelete = scenario.status !== 'published';

  return (
    <div className={`sp-card ${isActive ? 'sp-card-active' : ''}`}>
      <div className="sp-card-header">
        <div className="sp-card-info">
          <span className="sp-card-name">{scenario.name}</span>
          <div className="sp-card-badges">
            <span className={`sp-badge ${TYPE_COLORS[scenario.type]}`}>
              {TYPE_LABELS[scenario.type]}
            </span>
            {scenario.status === 'published' && (
              <span className="sp-badge badge-published">Publicado</span>
            )}
            {isActive && (
              <span className="sp-badge badge-active">Activo</span>
            )}
          </div>
        </div>
        <div className="sp-card-actions">
          {isActive ? (
            <button
              className="sp-icon-btn sp-icon-btn-active"
              onClick={onDeactivate}
              title="Desactivar escenario"
            >
              <Square size={14} />
            </button>
          ) : (
            <button
              className="sp-icon-btn"
              onClick={() => onActivate(scenario)}
              title="Activar escenario"
            >
              <Play size={14} />
            </button>
          )}
          {canDelete && (
            <button
              className="sp-icon-btn sp-icon-btn-danger"
              onClick={() => onDelete(scenario.id)}
              title="Eliminar escenario"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            className="sp-icon-btn"
            onClick={() => setExpanded(v => !v)}
            title={expanded ? 'Ocultar detalles' : 'Ver detalles'}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="sp-card-details">
          <span className="sp-detail-row">v{scenario.version}</span>
          {scenario.description && (
            <span className="sp-detail-row sp-detail-desc">{scenario.description}</span>
          )}
          <span className="sp-detail-row">
            {new Date(scenario.createdAt).toLocaleDateString('es', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────

export function ScenariosPanel(): JSX.Element {
  const { projectId } = useProject();

  const [scenarios, setScenarios]       = useState<Scenario[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [creating, setCreating]         = useState(false);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [deletingId, setDeletingId]     = useState<string | null>(null);

  // ── Load scenarios ────────────────────
  const loadScenarios = useCallback(async (): Promise<void> => {
    if (!projectId) { setScenarios([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await scenarioApi.listByProject(projectId);
      setScenarios(data);
    } catch {
      setError('Error al cargar escenarios');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void loadScenarios(); }, [loadScenarios]);

  // ── Listen for deactivation from banner ─
  useEffect(() => {
    const handler = (): void => { setActiveScenarioId(null); };
    window.addEventListener('scenario-deactivated', handler);
    return () => window.removeEventListener('scenario-deactivated', handler);
  }, []);

  // ── Handlers ─────────────────────────
  const handleActivate = (scenario: Scenario): void => {
    setActiveScenarioId(scenario.id);
    window.dispatchEvent(new CustomEvent('scenario-activated', { detail: scenario }));
  };

  const handleDeactivate = (): void => {
    setActiveScenarioId(null);
    window.dispatchEvent(new CustomEvent('scenario-deactivated'));
  };

  const handleCreated = (scenario: Scenario): void => {
    setScenarios(prev => [...prev, scenario]);
    setCreating(false);
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (id === activeScenarioId) handleDeactivate();
    setDeletingId(id);
    try {
      await scenarioApi.remove(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch {
      setError('Error al eliminar el escenario');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Render ────────────────────────────
  return (
    <div className="sp-panel">

      {/* Header */}
      <div className="sp-header">
        <div className="sp-header-title">
          <GitBranch size={16} />
          <span>Planificación</span>
        </div>
        {projectId && !creating && (
          <button
            className="sp-btn sp-btn-primary sp-btn-sm"
            onClick={() => setCreating(true)}
            title="Nuevo escenario"
          >
            <Plus size={14} />
            Nuevo
          </button>
        )}
      </div>

      {/* Body */}
      <div className="sp-body">

        {/* Create form */}
        {creating && projectId && (
          <CreateForm
            projectId={projectId}
            onCreated={handleCreated}
            onCancel={() => setCreating(false)}
          />
        )}

        {/* Error */}
        {error && (
          <div className="sp-error">
            <AlertCircle size={14} /><span>{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="sp-loading">
            <Loader2 size={20} className="sp-spin" />
          </div>
        )}

        {/* No project */}
        {!loading && !projectId && (
          <div className="sp-empty">
            <p>No hay proyecto activo.</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && projectId && !creating && scenarios.length === 0 && !error && (
          <div className="sp-empty">
            <GitBranch size={32} className="sp-empty-icon" />
            <p>Sin escenarios</p>
            <p className="sp-empty-sub">Crea un escenario para empezar a planificar</p>
          </div>
        )}

        {/* Scenarios list */}
        {!loading && scenarios.length > 0 && (
          <div className="sp-list">
            {scenarios.map(scenario => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                isActive={scenario.id === activeScenarioId}
                onActivate={handleActivate}
                onDeactivate={handleDeactivate}
                onDelete={(id) => { void handleDelete(id); }}
              />
            ))}
          </div>
        )}

        {deletingId && (
          <div className="sp-loading">
            <Loader2 size={16} className="sp-spin" />
            <span>Eliminando...</span>
          </div>
        )}
      </div>
    </div>
  );
}
